import type { JsonValue, TileRef } from "../document/types.ts";
import { parseUnitInput } from "./field-kinds.ts";

/**
 * The entity type definition of Playbook 5.7 and the JSON Schema subset the inspector builds forms from
 * (ADR-021 item 7): an object schema whose properties are string, number, integer, boolean or an enum of
 * strings, each with optional `title` and `default` (strings also `maxLength`), plus an optional
 * `required` list. A string property may cap its length and an integer property its least value. A property may
 * also be a reference to a prop or a marker of a registered type (ADR-021's amendment of 2026-09-27), whose
 * value is the target's id; long text; a number shown in the game's unit; cells painted on the map; or a list
 * of any of these but a list (ADR-021's amendment "the other field kinds").
 */

/**
 * The unit a number is shown in (ADR-021's amendment "the other field kinds", decision 2): its symbol, and how
 * many stored units make one shown (20 ticks a second).
 */
export interface FieldUnit {
  readonly symbol: string;
  readonly per: number;
}

/**
 * One property schema.
 */
export type PropertySchema =
  | { readonly type: "string"; readonly title?: string; readonly default?: string; readonly enum?: readonly string[]; readonly maxLength?: number; readonly multiline?: boolean; readonly lookup?: string }
  | { readonly type: "number"; readonly title?: string; readonly default?: number; readonly unit?: FieldUnit }
  | { readonly type: "integer"; readonly title?: string; readonly default?: number; readonly minimum?: number; readonly unit?: FieldUnit }
  | { readonly type: "boolean"; readonly title?: string; readonly default?: boolean }
  | { readonly type: "reference"; readonly title?: string; readonly target: ReferenceTarget }
  | { readonly type: "cells"; readonly title?: string }
  | { readonly type: "path"; readonly title?: string; readonly maxItems?: number; readonly closeWhen?: PathClose }
  | { readonly type: "list"; readonly title?: string; readonly items: ItemSchema; readonly maxItems?: number };

/**
 * What each row of a list holds: any property but a list or a path, without a title of its own.
 */
export type ItemSchema = Exclude<PropertySchema, { readonly type: "list" | "path" }>;

/**
 * When a path closes into a loop: the sibling choice that says so and the option that does. The path is drawn
 * with its last point joined back to its first while that choice holds the option.
 */
export interface PathClose {
  readonly field: string;
  readonly equals: string;
}

/**
 * What a reference field points at (ADR-021's amendment of 2026-09-27): a prop carrying an object type the
 * game registers, or a marker of an entity type it registers.
 */
export interface ReferenceTarget {
  readonly kind: "prop" | "marker";
  readonly type: string;
}

/**
 * An entity's field schema.
 */
export interface JsonSchema {
  readonly type: "object";
  readonly properties: { readonly [name: string]: PropertySchema };
  readonly required?: readonly string[];
}

/**
 * An entity type a game registers (Playbook 5.7). A type marked `retired` stays registered, so a saved entity of
 * it loads, compiles, shows its label and form and is edited and deleted as any other, and the editor does not
 * offer it for placing (`offeredEntityTypes`). Nothing else changes: a tool or an operation may still add one,
 * and a reference field that takes the type still takes a placed one.
 */
export interface EntityTypeDefinition {
  readonly type: string;
  readonly label: string;
  readonly icon?: TileRef;
  readonly schema: JsonSchema;
  readonly serverOnly: boolean;
  readonly retired?: boolean;
}

/**
 * A form field derived from the schema. A reference carries its `target`, a number with a unit its `unit`, a
 * list its `items` (the field each row is, under the list's name and title) and its `maxItems`, and a lookup the
 * name of the list its value is chosen from (ADR-021's amendment of 2026-10-02). A choice whose owner labels its
 * options carries `optionLabels`, one for each of `options` in order, shown in their place and never stored
 * (`withOptionLabels`, ADR-058's amendment of 2026-10-13).
 */
export interface SchemaField {
  readonly name: string;
  readonly kind: "string" | "text" | "number" | "integer" | "boolean" | "enum" | "reference" | "cells" | "path" | "list" | "lookup";
  readonly title: string;
  readonly required: boolean;
  readonly defaultValue: JsonValue | null;
  readonly options: readonly string[] | null;
  readonly optionLabels?: readonly string[];
  readonly maxLength: number | null;
  readonly minimum: number | null;
  readonly target?: ReferenceTarget;
  readonly unit?: FieldUnit;
  readonly items?: SchemaField;
  readonly maxItems?: number;
  readonly closeWhen?: PathClose;
  readonly lookup?: string;
}

/**
 * Result of parsing one field's text input.
 */
export type FieldParse = { readonly ok: true; readonly value: JsonValue | null } | { readonly ok: false; readonly error: string };

const ALLOWED_KEYS: Readonly<Record<PropertySchema["type"], readonly string[]>> = {
  string: ["type", "title", "default", "enum", "maxLength", "multiline", "lookup"],
  number: ["type", "title", "default", "unit"],
  integer: ["type", "title", "default", "minimum", "unit"],
  boolean: ["type", "title", "default"],
  reference: ["type", "title", "target"],
  cells: ["type", "title"],
  path: ["type", "title", "maxItems", "closeWhen"],
  list: ["type", "title", "items", "maxItems"],
};

const REFERENCE_SHAPE = "needs a target of kind prop or marker and a type";

/**
 * A lookup's list name: lower-case letters, digits and `-`.
 */
const LOOKUP_NAME = /^[a-z0-9-]+$/;

/**
 * Turns a schema into form fields in property order.
 *
 * @param schema Entity field schema.
 * @returns The fields.
 */
export function fieldsFromSchema(schema: JsonSchema): SchemaField[] {
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties).map(([name, property]) => fieldOf(name, property, property.title ?? name, required.has(name)));
}

/**
 * Gives the choices among some fields the labels their owner declares for them (an object type's
 * `choiceLabels`): a choice named there with one label an option carries them as `optionLabels`; every other
 * field is returned as it is.
 *
 * @param fields The fields.
 * @param labels The labels by field name, one for each option in order, or undefined for none.
 * @returns The fields, in order.
 */
export function withOptionLabels(fields: readonly SchemaField[], labels: { readonly [field: string]: readonly string[] } | undefined): SchemaField[] {
  return fields.map((field) => {
    const named = labels?.[field.name];
    return named !== undefined && field.options !== null && named.length === field.options.length ? { ...field, optionLabels: [...named] } : field;
  });
}

/**
 * One property's form field. A list's rows are its `items` under the list's name and title, each required,
 * since a row is never empty.
 *
 * @param name The property's name.
 * @param property The property.
 * @param title The field's title.
 * @param required Whether a value is required.
 * @returns The field.
 */
function fieldOf(name: string, property: PropertySchema, title: string, required: boolean): SchemaField {
  const isEnum = property.type === "string" && property.enum !== undefined;
  const isText = property.type === "string" && property.multiline === true;
  const lookup = property.type === "string" && property.lookup !== undefined ? property.lookup : undefined;
  const declared = property.type === "reference" || property.type === "cells" || property.type === "path" || property.type === "list" ? undefined : property.default;
  const unit = (property.type === "number" || property.type === "integer") && property.unit !== undefined ? property.unit : undefined;
  return {
    name,
    kind: isEnum ? "enum" : isText ? "text" : lookup !== undefined ? "lookup" : property.type,
    title,
    required,
    defaultValue: declared ?? null,
    options: isEnum ? [...(property as { enum: readonly string[] }).enum] : null,
    maxLength: property.type === "string" && property.maxLength !== undefined ? property.maxLength : null,
    minimum: property.type === "integer" && property.minimum !== undefined ? property.minimum : null,
    ...(property.type === "reference" ? { target: { kind: property.target.kind, type: property.target.type } } : {}),
    ...(unit === undefined ? {} : { unit: { symbol: unit.symbol, per: unit.per } }),
    ...(property.type === "list" ? { ...(property.maxItems === undefined ? {} : { maxItems: property.maxItems }), items: fieldOf(name, property.items, title, true) } : {}),
    ...(property.type === "path" ? { ...(property.maxItems === undefined ? {} : { maxItems: property.maxItems }), ...(property.closeWhen === undefined ? {} : { closeWhen: { field: property.closeWhen.field, equals: property.closeWhen.equals } }) } : {}),
    ...(lookup === undefined ? {} : { lookup }),
  };
}

/**
 * Parses text typed into a field. Long text keeps its line breaks as `\n`; a number with a unit is typed in
 * the shown unit and stored in the game's. Cells and lists are never typed, and are refused naming how they
 * are set.
 *
 * @param field The field.
 * @param text The input text.
 * @returns The typed value (null for an empty optional field), or an error naming the field.
 */
export function parseFieldInput(field: SchemaField, text: string): FieldParse {
  if (field.kind === "cells") {
    return { ok: false, error: `${field.title} is painted on the map, not typed` };
  }
  if (field.kind === "path") {
    return { ok: false, error: `${field.title} is placed on the map, not typed` };
  }
  if (field.kind === "list") {
    return { ok: false, error: `${field.title} is edited row by row, not typed` };
  }
  const typed = field.kind === "text" ? text.replace(/\r\n?/g, "\n") : text;
  const trimmed = typed.trim();
  if (trimmed === "") {
    return field.required ? { ok: false, error: `${field.title} is required` } : { ok: true, value: null };
  }
  switch (field.kind) {
    case "reference":
    case "lookup":
      return { ok: true, value: trimmed };
    case "string":
    case "text":
      return field.maxLength !== null && typed.length > field.maxLength ? { ok: false, error: `${field.title} must be at most ${field.maxLength} characters` } : { ok: true, value: typed };
    case "enum":
      return field.options?.includes(trimmed) ? { ok: true, value: trimmed } : { ok: false, error: `${field.title} must be one of ${(field.options ?? []).join(", ")}` };
    case "boolean":
      if (trimmed === "true" || trimmed === "false") {
        return { ok: true, value: trimmed === "true" };
      }
      return { ok: false, error: `${field.title} must be true or false` };
    case "number": {
      if (field.unit !== undefined) {
        return parseUnitInput(field, field.unit, trimmed);
      }
      const value = Number(trimmed);
      return Number.isFinite(value) ? { ok: true, value } : { ok: false, error: `${field.title} must be a number` };
    }
    case "integer": {
      if (field.unit !== undefined) {
        return parseUnitInput(field, field.unit, trimmed);
      }
      const value = Number(trimmed);
      if (!Number.isInteger(value)) {
        return { ok: false, error: `${field.title} must be an integer` };
      }
      return field.minimum !== null && value < field.minimum ? { ok: false, error: `${field.title} must be at least ${field.minimum}` } : { ok: true, value };
    }
  }
}

/**
 * Checks a field schema's properties against the supported subset (ADR-021 decision 7 and its amendments): an
 * object of properties, each checked by `validateProperty`. An entity type's schema and a game's map schema
 * (ADR-052's amendment of 2026-09-26) are both checked by it, with `validateRequired`.
 *
 * @param schema The schema.
 * @param owner What declares it, leading every message ("entity type marker", "map schema").
 * @throws Error naming the owner and the property or keyword that is not supported.
 */
function validateProperties(schema: JsonSchema, owner: string): void {
  if (schema.type !== "object" || typeof schema.properties !== "object") {
    throw new Error(`${owner}: schema must be an object with properties`);
  }
  for (const [name, property] of Object.entries(schema.properties)) {
    validateProperty(property, `${owner}: property ${name}`, false);
    if (property.type === "path" && property.closeWhen !== undefined) {
      validateCloseSibling(property.closeWhen, schema, `${owner}: property ${name}`);
    }
  }
}

/**
 * Checks that the choice a path closes on is a sibling choice that offers the option.
 *
 * @param close The path's `closeWhen`, already checked for shape.
 * @param schema The schema the path belongs to.
 * @param lead The message's lead, naming the owner and the property.
 * @throws Error naming the field or option that does not exist.
 */
function validateCloseSibling(close: PathClose, schema: JsonSchema, lead: string): void {
  const sibling = schema.properties[close.field];
  if (sibling === undefined || sibling.type !== "string" || sibling.enum === undefined) {
    throw new Error(`${lead} closeWhen names ${close.field}, which is not a choice of this type`);
  }
  if (!sibling.enum.includes(close.equals)) {
    throw new Error(`${lead} closeWhen names ${close.equals}, which ${close.field} does not offer`);
  }
}

/**
 * Checks one property: a supported type with only that type's keywords (a list's rows without a title), a
 * string's `maxLength` a positive integer, `multiline` true and never with an enum, an integer's `minimum` an
 * integer, a unit's shape, a reference's target, and a list's `items` by these same rules and its `maxItems` a
 * positive integer.
 *
 * @param property The property.
 * @param lead The message's lead, naming the owner and the property ("entity type notice: property names").
 * @param isItem Whether it is a list's rows.
 * @throws Error naming what is not supported.
 */
function validateProperty(property: PropertySchema, lead: string, isItem: boolean): void {
  const allowed = ALLOWED_KEYS[property.type as PropertySchema["type"]];
  if (allowed === undefined) {
    throw new Error(`${lead} has unsupported type ${String((property as { type: unknown }).type)}`);
  }
  for (const key of Object.keys(property)) {
    if (!allowed.includes(key) || (isItem && key === "title")) {
      throw new Error(`${lead} uses unsupported keyword ${key}`);
    }
  }
  if (property.type === "string") {
    if (property.maxLength !== undefined && (!Number.isInteger(property.maxLength) || property.maxLength < 1)) {
      throw new Error(`${lead} maxLength must be a positive integer`);
    }
    if (property.multiline !== undefined && property.multiline !== true) {
      throw new Error(`${lead} multiline must be true`);
    }
    if (property.multiline === true && property.enum !== undefined) {
      throw new Error(`${lead} is multiline and a choice; a choice is not typed`);
    }
    if (property.lookup !== undefined) {
      validateLookup(property, lead, isItem);
    }
  }
  if (property.type === "integer" && property.minimum !== undefined && !Number.isInteger(property.minimum)) {
    throw new Error(`${lead} minimum must be an integer`);
  }
  if ((property.type === "number" || property.type === "integer") && property.unit !== undefined) {
    validateUnit(property.unit as unknown, `${lead} unit`);
  }
  if (property.type === "reference") {
    validateReferenceShape(property.target as unknown, `${lead} reference`);
  }
  if (property.type === "list") {
    validateList(property, lead);
  }
  if (property.type === "path") {
    validatePath(property, lead, isItem);
  }
}

/**
 * Checks a path's limit and the shape of the choice that closes it; the sibling it names is checked with the
 * schema (`validateCloseSibling`).
 *
 * @param property The path.
 * @param lead The message's lead.
 * @param isItem Whether it is a list's items.
 * @throws Error naming what is wrong.
 */
function validatePath(property: Extract<PropertySchema, { readonly type: "path" }>, lead: string, isItem: boolean): void {
  if (isItem) {
    throw new Error(`${lead} is a path; a list of paths is not built`);
  }
  if (property.maxItems !== undefined && (!Number.isInteger(property.maxItems) || property.maxItems < 1)) {
    throw new Error(`${lead} maxItems must be a positive integer`);
  }
  const close = property.closeWhen as unknown;
  if (close === undefined) {
    return;
  }
  const record = typeof close === "object" && close !== null && !Array.isArray(close) ? (close as Record<string, unknown>) : null;
  if (record === null || typeof record["field"] !== "string" || record["field"] === "" || typeof record["equals"] !== "string" || record["equals"] === "" || Object.keys(record).some((key) => key !== "field" && key !== "equals")) {
    throw new Error(`${lead} closeWhen needs a field and a value it equals`);
  }
}

/**
 * Checks a list's rows and its limit.
 *
 * @param property The list.
 * @param lead The message's lead.
 * @throws Error naming what is wrong.
 */
function validateList(property: Extract<PropertySchema, { readonly type: "list" }>, lead: string): void {
  const items = property.items as unknown;
  if (typeof items !== "object" || items === null || Array.isArray(items)) {
    throw new Error(`${lead} needs items, the property each row holds`);
  }
  if ((items as { type?: unknown }).type === "list") {
    throw new Error(`${lead} items is a list; a row of rows is a record`);
  }
  validateProperty(items as ItemSchema, `${lead} items`, true);
  if (property.maxItems !== undefined && (!Number.isInteger(property.maxItems) || property.maxItems < 1)) {
    throw new Error(`${lead} maxItems must be a positive integer`);
  }
}

/**
 * Checks a lookup (ADR-021's amendment of 2026-10-02, decision 6): a list's name, on a string that is no fixed
 * choice, no long text and no limit, and not a list's rows.
 *
 * @param property The string property.
 * @param lead The message's start.
 * @param isItem Whether it is a list's items.
 * @throws Error naming what is wrong.
 */
function validateLookup(property: Extract<PropertySchema, { readonly type: "string" }>, lead: string, isItem: boolean): void {
  if (typeof property.lookup !== "string" || !LOOKUP_NAME.test(property.lookup)) {
    throw new Error(`${lead} lookup must name a list in lower-case letters, digits and -`);
  }
  if (isItem) {
    throw new Error(`${lead} is a lookup; a list of lookups is not built`);
  }
  if (property.enum !== undefined) {
    throw new Error(`${lead} is a lookup and a fixed choice; choose one`);
  }
  if (property.multiline !== undefined || property.maxLength !== undefined) {
    throw new Error(`${lead} is a lookup, which is chosen, not typed`);
  }
}

/**
 * Checks a unit: a non-empty symbol, a positive finite per, nothing else.
 *
 * @param unit The declared unit.
 * @param lead The message's lead, naming the owner and the property.
 * @throws Error naming what is wrong.
 */
function validateUnit(unit: unknown, lead: string): void {
  const record = typeof unit === "object" && unit !== null && !Array.isArray(unit) ? (unit as Record<string, unknown>) : null;
  const per = record?.["per"];
  if (record === null || typeof record["symbol"] !== "string" || record["symbol"] === "" || typeof per !== "number" || !Number.isFinite(per) || per <= 0) {
    throw new Error(`${lead} needs a non-empty symbol and a positive per`);
  }
  for (const key of Object.keys(record)) {
    if (key !== "symbol" && key !== "per") {
      throw new Error(`${lead} uses unsupported keyword ${key}`);
    }
  }
}

/**
 * Checks a reference's target object: a kind of prop or marker, a non-empty type, nothing else. A zone is
 * named apart, since it is the kind that waits for its layer (P1.3-T3).
 *
 * @param target The declared target.
 * @param lead The message's lead, naming the owner and the property.
 * @throws Error naming what is wrong.
 */
function validateReferenceShape(target: unknown, lead: string): void {
  if (typeof target !== "object" || target === null || Array.isArray(target)) {
    throw new Error(`${lead} ${REFERENCE_SHAPE}`);
  }
  const record = target as Record<string, unknown>;
  const kind = record["kind"];
  if (kind === "zone") {
    throw new Error(`${lead} targets a zone, which waits for zone layers (P1.3-T3)`);
  }
  if (typeof kind === "string" && kind !== "prop" && kind !== "marker") {
    throw new Error(`${lead} targets kind ${kind}; a reference names a prop or a marker`);
  }
  if ((kind !== "prop" && kind !== "marker") || typeof record["type"] !== "string" || record["type"] === "") {
    throw new Error(`${lead} ${REFERENCE_SHAPE}`);
  }
  for (const key of Object.keys(record)) {
    if (key !== "kind" && key !== "type") {
      throw new Error(`${lead} target uses unsupported keyword ${key}`);
    }
  }
}

/**
 * Checks that every name a schema requires is one of its properties.
 *
 * @param schema The schema.
 * @param owner What declares it, leading the message.
 * @throws Error naming the owner and the required name no property carries.
 */
function validateRequired(schema: JsonSchema, owner: string): void {
  for (const name of schema.required ?? []) {
    if (!(name in schema.properties)) {
      throw new Error(`${owner}: required property ${name} is not defined`);
    }
  }
}

/**
 * Checks a field schema a game registers outside any type, a map schema (ADR-052's amendment of 2026-09-26),
 * against the subset an entity type's schema keeps to, less what needs a thing on the map: a reference, which
 * has no end to draw a link from, and cells, which have no anchor to count from, alone or in a list.
 *
 * @param schema The schema.
 * @param owner What declares it, leading every message.
 * @throws Error naming the owner and the property, keyword or required name that is not supported.
 */
export function validateSchema(schema: JsonSchema, owner: string): void {
  validateProperties(schema, owner);
  for (const [name, property] of Object.entries(schema.properties)) {
    const kind = property.type === "list" ? property.items.type : property.type;
    const words = kind === "reference" ? "a reference" : kind === "cells" ? "cells" : kind === "path" ? "a path" : null;
    if (words !== null) {
      const plural = kind === "reference" ? "references" : "cells";
      throw new Error(`${owner}: property ${name} is ${property.type === "list" ? `a list of ${plural}` : words}, which only a type's fields may declare`);
    }
  }
  validateRequired(schema, owner);
}

/**
 * The reference a property declares, alone or as each row of a list.
 *
 * @param property The property.
 * @returns The target, or undefined for a property that is no reference.
 */
export function referenceTargetOf(property: PropertySchema): ReferenceTarget | undefined {
  if (property.type === "reference") {
    return property.target;
  }
  return property.type === "list" && property.items.type === "reference" ? property.items.target : undefined;
}

/**
 * Checks every reference field of the registered types against the types themselves (ADR-021's amendment of
 * 2026-09-27, decision 5): a prop reference names an object type the game registers that is not driven by
 * presence, since such a prop lowers to no entity the game could find; a marker reference names an entity
 * type it registers. A list of references is checked as one reference. Run where every type is known: at
 * registration and when a contract is read.
 *
 * @param entityTypes The registered entity (marker) types.
 * @param objectTypes The registered object types.
 * @throws Error naming the declaring type, the field and what its target lacks.
 */
export function validateReferenceTargets(entityTypes: readonly EntityTypeDefinition[], objectTypes: readonly { readonly type: string; readonly schema: JsonSchema; readonly presence?: unknown }[]): void {
  const owners = [...entityTypes.map((t) => ({ owner: `entity type ${t.type}`, schema: t.schema })), ...objectTypes.map((t) => ({ owner: `object type ${t.type}`, schema: t.schema }))];
  for (const { owner, schema } of owners) {
    for (const [name, property] of Object.entries(schema.properties)) {
      const declared = referenceTargetOf(property);
      if (declared === undefined) {
        continue;
      }
      const { kind, type } = declared;
      if (kind === "marker") {
        if (!entityTypes.some((t) => t.type === type)) {
          throw new Error(`${owner}: field ${name} references marker type ${type}, which the game does not register as a marker type`);
        }
        continue;
      }
      const target = objectTypes.find((t) => t.type === type);
      if (target === undefined) {
        throw new Error(`${owner}: field ${name} references prop type ${type}, which the game does not register as an object type`);
      }
      if (target.presence !== undefined) {
        throw new Error(`${owner}: field ${name} references prop type ${type}, which is driven by presence, so the game never receives it`);
      }
    }
  }
}

/**
 * Checks an entity type definition against the supported subset.
 *
 * @param definition The definition.
 * @throws Error naming the type, property or keyword that is not supported.
 */
export function validateEntityType(definition: EntityTypeDefinition): void {
  if (definition.type === "") {
    throw new Error("entity type must have a non-empty type");
  }
  const { schema } = definition;
  validateProperties(schema, `entity type ${definition.type}`);
  validateRequired(schema, `entity type ${definition.type}`);
}
